// What the formula editor suggests at the cursor: functions and fields for the word being
// typed, the fields of a scope or a linked record inside `[…]`, units after a number.

import type { FilterSchema } from "../types";
import type { FormulaFunction, FormulaScope } from "./api";
import { isWordStart, unitOf } from "./lexer";

export type CompletionItem = {
    key: string;
    kind: "function" | "field" | "relation" | "scope" | "unit";
    /** Main text (a function's name, a field's path). */
    label: string;
    /** Under or after the label: a signature, a help line, a type. */
    detail?: string;
    help?: string;
    scope?: FormulaScope;
    /** Replaces `from`…`to` of the text. */
    insert: string;
    /** Where the cursor goes in `insert` (default: its end). */
    caret?: number;
    /** A scope or a linked record: inserting it goes on completing inside it. */
    continues?: boolean;
};

export type Completion = {
    items: CompletionItem[];
    /** What an item replaces: `from`…`to` (to the closing bracket inside a field). */
    from: number;
    to: number;
    /** What an item that `continues` replaces ends here: the segment being typed, and its dot. */
    segmentEnd: number;
    query: string;
};

export type ScopeSchema = { scope: FormulaScope; schema: FilterSchema };

const TYPE_LABELS: Record<string, string> = {
    text: "Text",
    choice: "Choice",
    picklist: "Choice",
    number: "Number",
    date: "Date",
    datetime: "Date & time",
    record: "Record"
};

export const fold = (text: string) => text.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

/**
 * How a field or a relation is written in a formula: its label, or its schema name when another
 * of the object shares that label (as the server's `shown_label`): the text always names one field.
 */
export function shownLabel(schema: FilterSchema, item: { name: string; label: string }): string {
    const label = item.label.toLowerCase();
    const same = formulaFields(schema).filter(field => field.label.toLowerCase() === label).length + formulaRelations(schema).filter(relation => relation.label.toLowerCase() === label).length;
    return same > 1 || item.label.includes("]") || item.label.includes(".") ? item.name : item.label;
}

/** Fields a formula can read on a schema (the server marks them with their formula operators). */
export function formulaFields(schema: FilterSchema) {
    return schema.fields.filter(field => field.formula_operators?.length);
}

/** To-one relations a formula can go through (and, when marked, read as a record). */
export function formulaRelations(schema: FilterSchema) {
    return schema.relations.filter(relation => relation.kind === "to_one");
}

function rank(label: string, query: string): number {
    const haystack = fold(label);
    const needle = fold(query);
    if (!needle) return 1;
    if (haystack.startsWith(needle)) return 0;
    if (haystack.split(/[\s._›-]+/).some(word => word.startsWith(needle))) return 1;
    return haystack.includes(needle) ? 2 : -1;
}

function ranked<T>(items: T[], label: (item: T) => string, query: string, limit: number, preferred: (item: T) => boolean = () => true): T[] {
    return (
        items
            .map(item => ({ item, score: rank(label(item), query), preferred: preferred(item) }))
            .filter(entry => entry.score >= 0)
            // The best match first; among equal ones, the fields of the compared field's kind.
            .sort((a, b) => a.score - b.score || Number(b.preferred) - Number(a.preferred))
            .slice(0, limit)
            .map(entry => entry.item)
    );
}

const FAMILIES: Record<string, string> = { text: "text", choice: "text", picklist: "text", date: "date", datetime: "date", number: "number", record: "record" };

/** Fields of the same kind as the compared one come first: they compare with it as they are. */
export function sameFamily(type: string | undefined, compared: string | undefined): boolean {
    return !compared || FAMILIES[type ?? ""] === FAMILIES[compared];
}

/**
 * The completion at `caret`. `schemaOf` gives the schema of a linked model when it is loaded
 * (`needs` lists those still to load, the caller loads them and asks again).
 */
export function completeAt(
    text: string,
    caret: number,
    scopes: ScopeSchema[],
    functions: FormulaFunction[],
    schemaOf: (model: string) => FilterSchema | undefined,
    /** The type of the compared field: fields of its kind are suggested first. */
    comparedType?: string
): Completion & { needs: string[] } {
    const none = { items: [], from: caret, to: caret, segmentEnd: caret, query: "", needs: [] };
    if (!scopes.length) return none;
    const before = text.slice(0, caret);
    const open = before.lastIndexOf("[");
    if (open >= 0 && !before.slice(open).includes("]")) return completeField(text, caret, open, scopes, schemaOf, comparedType);

    const word = /[\p{L}_][\p{L}\p{N}_]*$/u.exec(before);
    if (!word || !isWordStart(word[0][0])) return none;
    const from = caret - word[0].length;
    const after = /^[\p{L}\p{N}_]*/u.exec(text.slice(caret))?.[0] ?? "";
    const to = caret + after.length;
    const query = word[0];

    // « 10 da » → days.
    if (/\d\s*$/.test(text.slice(0, from))) {
        // A unit typed in full needs nothing more.
        if (unitOf(query)) return { ...none, from, to, segmentEnd: to, query };
        const units = ["days", "weeks", "months", "years"].filter(unit => unit.startsWith(query.toLowerCase()));
        return { items: units.map(unit => ({ key: `unit:${unit}`, kind: "unit", label: unit, insert: unit })), from, to, segmentEnd: to, query, needs: [] };
    }

    const items: CompletionItem[] = ranked(functions, item => item.name, query, 3).map(item => {
        const call = `${item.name}()`;
        return {
            key: `fn:${item.name}`,
            kind: "function",
            label: item.name,
            detail: item.signature.slice(item.name.length),
            help: item.help,
            insert: call,
            // Inside the brackets, unless there is nothing to put there.
            caret: item.signature.endsWith("()") ? call.length : item.name.length + 1
        };
    });
    const fields = scopes.flatMap(({ scope, schema }) => formulaFields(schema).map(field => ({ scope, field, schema })));
    for (const { scope, field, schema } of ranked(
        fields,
        entry => entry.field.label,
        query,
        8 - items.length,
        entry => sameFamily(entry.field.type, comparedType)
    )) {
        items.push({
            key: `field:${scope.index}:${field.name}`,
            kind: "field",
            label: `${scope.name} › ${field.label}`,
            detail: TYPE_LABELS[field.type] ?? "",
            scope,
            insert: `[${scope.name}.${shownLabel(schema, field)}]`
        });
    }
    return { items, from, to, segmentEnd: to, query, needs: [] };
}

function completeField(
    text: string,
    caret: number,
    open: number,
    scopes: ScopeSchema[],
    schemaOf: (model: string) => FilterSchema | undefined,
    comparedType?: string
): Completion & { needs: string[] } {
    const content = text.slice(open + 1, caret);
    const segments = content.split(".");
    const query = segments.pop() ?? "";
    // Up to the closing bracket when the cursor is inside a written field.
    const rest = text.slice(caret);
    const close = rest.indexOf("]");
    const nextOpen = rest.indexOf("[");
    const to = close >= 0 && (nextOpen < 0 || close < nextOpen) && !rest.slice(0, close).includes("\n") ? caret + close + 1 : caret;
    const segment = /^[^.\][\n]*/.exec(rest)?.[0].length ?? 0;
    const segmentEnd = caret + segment + (rest[segment] === "." ? 1 : 0);

    let scope: ScopeSchema | undefined;
    let schema: FilterSchema | undefined;
    const path: string[] = [];
    const items: CompletionItem[] = [];
    if (segments.length) {
        scope = scopes.find(item => item.scope.name.toLowerCase() === segments[0].trim().toLowerCase());
        const hops = scope ? segments.slice(1) : segments;
        scope = scope ?? scopes[scopes.length - 1];
        schema = scope.schema;
        for (const hop of hops) {
            const written = hop.trim();
            const relations = formulaRelations(schema);
            const relation = relations.find(item => item.name === written) ?? relations.find(item => item.label.toLowerCase() === written.toLowerCase());
            if (!relation) return { items: [], from: open, to, segmentEnd, query, needs: [] };
            const next = schemaOf(relation.model);
            if (!next) return { items: [], from: open, to, segmentEnd, query, needs: [relation.model] };
            path.push(shownLabel(schema, relation));
            schema = next;
        }
    } else {
        for (const item of ranked(scopes, entry => entry.scope.name, query, scopes.length)) {
            items.push({ key: `scope:${item.scope.index}`, kind: "scope", label: item.scope.name, detail: "Fields of", scope: item.scope, insert: `[${item.scope.name}.`, continues: true });
        }
        scope = scopes[scopes.length - 1];
        schema = scope.schema;
    }
    const prefix = [scope.scope.name, ...path];
    const fields = ranked(
        formulaFields(schema),
        field => field.label,
        query,
        12,
        field => sameFamily(field.type, comparedType)
    );
    for (const field of fields) {
        items.push({
            key: `field:${scope.scope.index}:${[...path, field.name].join(".")}`,
            kind: "field",
            label: [...prefix, field.label].join(" › "),
            detail: TYPE_LABELS[field.type] ?? "",
            scope: scope.scope,
            insert: `[${[...prefix, shownLabel(schema, field)].join(".")}]`
        });
    }
    for (const relation of ranked(formulaRelations(schema), item => item.label, query, 8)) {
        items.push({
            key: `relation:${scope.scope.index}:${[...path, relation.name].join(".")}`,
            kind: "relation",
            label: [...prefix, relation.label].join(" › "),
            detail: "Fields of",
            scope: scope.scope,
            insert: `[${[...prefix, shownLabel(schema, relation)].join(".")}.`,
            continues: true
        });
    }
    return { items: items.slice(0, 14), from: open, to, segmentEnd, query, needs: [] };
}

/** `text` with `from`…`to` replaced by `insert`, and where the cursor goes. */
export function applyCompletion(
    text: string,
    completion: Pick<Completion, "from" | "to" | "segmentEnd">,
    item: Pick<CompletionItem, "insert" | "caret" | "continues">
): { text: string; caret: number } {
    // A scope or a linked record goes on completing: what follows its segment stays.
    const to = item.continues ? completion.segmentEnd : completion.to;
    const next = text.slice(0, completion.from) + item.insert + text.slice(to);
    return { text: next, caret: completion.from + (item.caret ?? item.insert.length) };
}
